import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Factory, ShieldCheck, Globe, ChevronRight, Cpu, Zap, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS, scrollToSection } from '@/lib/index';
import { productionLines, coatingFacilities, productCategories, companyStats } from '@/data/index';
import { ProductionLineCard, CoatingFacilityCard, ProductCard, StatsCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { Button } from '@/components/ui/button';
import { LMEWidget } from '@/components/LMEWidget';
export default function Home() {
  const heroSlides = [
    { src: IMAGES.HERO_INDUSTRIAL_4,    alt: "Alüminyum Fabrikası" },
    { src: IMAGES.ALUMINYUM_BILLET_NEDIR, alt: "Alüminyum Billet" },
    { src: IMAGES.ALUMINIUM_INGOT_99_7,   alt: "Alüminyum Külçe" },
  ];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] w-full flex items-center">
        <div className="absolute inset-0 z-0 overflow-hidden">
          {heroSlides.map((slide, index) => (
            <img
              key={index}
              src={slide.src}
              alt={slide.alt}
              className="absolute inset-0 w-full h-full object-cover brightness-[0.4] transition-opacity duration-1000"
              style={{ opacity: index === currentSlide ? 1 : 0 }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={springPresets.smooth} className="max-w-3xl text-white">
            <span className="inline-block py-1 px-3 rounded-sm bg-accent/20 border border-accent/30 text-accent text-sm font-medium mb-6 uppercase tracking-widest">
              Geleceğin Alüminyum Teknolojileri
            </span>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              Sınırları Zorlayan <br />
              <span className="text-accent">Entegre Üretim</span> Gücü
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-2xl font-light">
              Mimari sistemlerden endüstriyel profillere kadar, 4" - 5" - 6" - 7" ebatlarında ekstrüzyon hatlarımız ve yüksek kapasiteli boyama tesislerimizle küresel standartlarda üretim yapıyoruz.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-bold px-8 h-14">
                <Link to={ROUTE_PATHS.CONTACT}>
                  Teklif İste
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 px-8 h-14" onClick={() => scrollToSection('integrated-production')}>
                Üretimi Keşfet
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Slideshow Dots */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-10 lg:bottom-28">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-accent w-8'
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        {/* Floating Stats Quick View */}
        <div className="absolute bottom-0 left-0 w-full bg-white/5 backdrop-blur-md border-t border-white/10 hidden lg:block">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-4 gap-8">
              {companyStats.map(stat => <div key={stat.id} className="text-white">
                  <p className="text-3xl font-bold text-accent">{stat.value}{stat.suffix}</p>
                  <p className="text-sm text-gray-400 font-medium uppercase tracking-wider">{stat.label}</p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* LME Widget */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <LMEWidget />
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp}>
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Kurumsal Bakış
              </div>
              <h2 className="text-4xl font-bold mb-8 text-primary">
                5 Yılda Yükselen Güç, <br />
                Modern Mühendislik Yaklaşımı
              </h2>
              <div className="space-y-6 text-muted-foreground leading-relaxed">
                <p>
                  2020 yılında kurulan MIMALUM, alüminyum ekstrüzyon sektöründe güçlü ve teknoloji odaklı bir oyuncu olma vizyonuyla yola çıktı. Kısa sürede büyüyen tesislerimiz, 26.000 m² kapalı alanda 4 ekstrüzyon hattı, gelişmiş boyama tesisleri ve dökümhaneden nihai ürüne uzanan tam entegre bir üretim zincirini barındırmaktadır.
                </p>
                <p>
                  Sadece profil üretmiyor, mühendislik çözümleri sunuyoruz. Kalıp tasarımı, dökümhane, ekstrüzyon, yüzey işlem ve mekanik işlem birimlerimizle müşterilerimizin en karmaşık ihtiyaçlarına cevap veriyoruz.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">Yüksek Kalite</h4>
                    <p className="text-sm text-muted-foreground">Qualicoat ve ISO standartları</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="p-3 bg-primary/5 rounded-lg">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-primary">Global Erişim</h4>
                    <p className="text-sm text-muted-foreground">45'ten fazla ülkeye ihracat</p>
                  </div>
                </div>
              </div>
              <Button asChild variant="link" className="mt-8 p-0 text-primary font-bold hover:no-underline">
                <Link to={ROUTE_PATHS.COMPANY} className="flex items-center">
                  Şirket Profilini İncele <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative">
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                <img src="/images/Gemini_Generated_Image_wl5sy6wl5sy6wl5s.png" alt="Ekstrüzyon Hattı" className="w-full h-[333px] object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-primary p-10 rounded-xl text-white hidden md:block">
                <div className="text-5xl font-bold text-accent mb-2">5+</div>
                <div className="text-sm font-medium uppercase tracking-widest opacity-80">Yıllık Sektör <br /> Tecrübesi</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrated Production (Extrusion) */}
      <section id="integrated-production" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="mb-16">
            <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
              <div className="h-1 w-12 bg-primary" />
              Üretim Teknolojileri
            </div>
            <h2 className="text-4xl font-bold text-primary">Entegre Ekstrüzyon Hatları</h2>
            <p className="text-muted-foreground mt-4 max-w-2xl">
              Farklı ölçeklerdeki 4 adet modern ekstrüzyon hattımızla, her türlü teknik ve estetik gereksinime uygun profiller üretiyoruz.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-100px"
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productionLines.map(line => <motion.div key={line.id} variants={staggerItem}>
                <ProductionLineCard line={line} />
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Coating Facilities */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16 mb-20">
            <div className="w-full lg:w-1/3">
              <div className="flex items-center gap-2 text-primary font-bold mb-4 uppercase tracking-tighter">
                <div className="h-1 w-12 bg-primary" />
                Yüzey İşlem
              </div>
              <h2 className="text-4xl font-bold text-primary mb-6">Yüzey İşlem ve Boyama Tesisleri</h2>
              <p className="text-muted-foreground mb-8">
                2 adet yatay ve 1 adet dikey toz boya hattımızla yıllık 21.000 tonun üzerinde boyama kapasitesine sahibiz. Qualicoat Seaside standartlarında, sınırsız renk seçeneği sunuyoruz.
              </p>
              <ul className="space-y-4">
                {['2 Adet Yatay Toz Boya Hattı', '1 Adet Dikey Toz Boya Hattı', 'Kromat İçermeyen Yüzey Hazırlığı', 'Geniş RAL Kartelası Seçenekleri'].map((item, idx) => <li key={idx} className="flex items-center gap-3 font-medium text-primary">
                    <div className="h-2 w-2 rounded-full bg-accent" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="w-full lg:w-2/3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {coatingFacilities.map(facility => <CoatingFacilityCard key={facility.id} facility={facility} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-24 bg-primary text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none">
          <Layers className="w-full h-full stroke-[0.5]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Ürün Gruplarımız</h2>
            <p className="text-primary-foreground/70 max-w-2xl mx-auto">
              Mühendislik ve estetiğin buluştuğu noktada, projenize özel alüminyum çözümler üretiyoruz.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {productCategories.map(category => <ProductCard key={category.id} category={category} />)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-primary">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Projeniz İçin Teknik Destek mi Lazım?</h2>
              <p className="text-lg font-medium opacity-80">
                Uzman mühendislik ekibimiz, tasarım aşamasından üretime kadar yanınızda.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-primary text-white hover:bg-primary/90 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.CONTACT}>Teklif İsteyin</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/5 px-10 h-16 text-lg">
                <Link to={ROUTE_PATHS.DOWNLOAD_CENTER}>Katalogları İndir</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Highlight */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-green-50 rounded-full mb-8">
            <Zap className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-primary mb-6">%100 Geri Dönüştürülebilir Gelecek</h2>
          <p className="text-muted-foreground max-w-3xl mx-auto mb-10 text-lg">
            Alüminyum sonsuz kez geri dönüştürülebilir. Üretim tesislerimizde çevreye duyarlı teknolojiler kullanarak karbon ayak izimizi minimize ediyor, sürdürülebilir bir dünya için yeşil alüminyum üretiyoruz.
          </p>
          <Button asChild variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
            <Link to={ROUTE_PATHS.SUSTAINABILITY}>Sürdürülebilirlik Vizyonumuz</Link>
          </Button>
        </div>
      </section>
    </div>;
}